import { IMAGES } from "./content";

// Order matters — used on the Services overview page.
export const SERVICE_DETAILS = {
  "general-contracting": {
    slug: "general-contracting",
    name: "General Contracting",
    short: "Comprehensive construction leadership focused on coordinating trades, managing daily operations, and delivering quality workmanship throughout the project.",
    image: "/media/generated/general-contractor.jpg",
    hero: {
      eyebrow: "Services",
      titleLines: ["General", "Contracting"],
      tagline: "Your Trusted Partner for Successful Multifamily Construction",
      description:
        "Next Horizon Builders delivers complete general contracting services with a focus on quality, safety, accountability, and on-time project delivery. We turn plans into exceptional communities.",
      cta: { label: "Request a Consultation", to: "/contact" },
    },
    intro: {
      eyebrow: "Overview",
      title: "Full-Service General Contracting",
      body: [
        "As a licensed and insured general contractor, Next Horizon Builders oversees every aspect of the construction process—managing people, materials, schedules, and quality from groundbreaking to final turnover.",
        "We are fully responsible for the successful execution of your project, coordinating all trades, managing risk, and ensuring code compliance so our clients can build with confidence.",
      ],
    },
    cards: {
      eyebrow: "Benefits",
      title: "Why owners build with us.",
      items: [
        { title: "Single Point of Responsibility", body: "We serve as your single point of contact throughout the entire construction process." },
        { title: "Experienced Field Leadership", body: "Our experienced professionals provide hands-on supervision and proactive project management." },
        { title: "Cost & Schedule Accountability", body: "Careful planning and continuous monitoring help protect both schedule and budget." },
        { title: "Quality Construction", body: "Every phase is completed with precision, craftsmanship, and attention to detail." },
        { title: "Safety-First Culture", body: "We maintain OSHA-compliant jobsites with safety integrated into every operation." },
        { title: "On-Time Project Delivery", body: "Efficient coordination and clear communication keep projects moving forward." },
      ],
    },
    role: {
      eyebrow: "Our Role",
      title: "Our Role as Your General Contractor",
      body: "We take complete ownership of construction execution by coordinating subcontractors, maintaining schedules, enforcing quality standards, and ensuring every project is delivered safely, efficiently, and according to plan.",
    },
    lists: [
      {
        eyebrow: "Scope",
        title: "What we manage.",
        items: [
          "Complete Project Oversight",
          "Subcontractor Coordination",
          "Construction Scheduling",
          "Budget & Cost Control",
          "Quality Assurance",
          "Safety Management",
          "Permitting & Code Compliance",
          "Owner Communication",
        ],
      },
      {
        eyebrow: "Why Next Horizon Builders",
        title: "Built on trust.",
        items: [
          "Specialized in Ground-Up Multifamily Construction",
          "Transparent Communication",
          "Reliable Project Leadership",
          "Commitment to Safety & Quality",
          "Long-Term Client Relationships",
          "Professional Field Management",
        ],
      },
    ],
    cta: {
      title: "Let's Build Something Great Together",
      body: "Partner with Next Horizon Builders for dependable general contracting services focused on quality, integrity, and successful project delivery.",
      button: { label: "Request a Consultation", to: "/contact" },
    },
  },

  "pre-construction": {
    slug: "pre-construction",
    name: "Pre-Construction",
    short: "Strategic planning services that evaluate project feasibility, develop realistic budgets, optimize scheduling, and prepare projects for successful execution.",
    image: IMAGES.team[2],
    listImage: "/media/generated/services-preconstruction-card.jpeg",
    hero: {
      eyebrow: "Services",
      titleLines: ["Pre-Construction"],
      tagline: "Smart planning today leads to successful construction tomorrow.",
      description:
        "Our pre-construction services provide the strategic planning, budgeting, scheduling, and project evaluation needed to establish a strong foundation before construction begins.",
      cta: { label: "Contact Us", to: "/contact" },
    },
    intro: {
      eyebrow: "Overview",
      title: "Plan Smarter. Build Better.",
      body: [
        "Successful projects begin long before construction starts. Our pre-construction services help owners, developers, and project teams evaluate feasibility, reduce risk, optimize budgets, and prepare projects for efficient execution.",
      ],
    },
    cards: {
      eyebrow: "Services",
      title: "How we prepare your project.",
      items: [
        { title: "Preliminary Cost Estimating", body: "Develop realistic project budgets and early cost projections." },
        { title: "Guaranteed Maximum Price (GMP) Development", body: "Create transparent pricing strategies that improve financial certainty." },
        { title: "Value Engineering", body: "Identify opportunities to maximize value while maintaining quality." },
        { title: "Constructability Reviews", body: "Evaluate design documents to improve efficiency and minimize future conflicts." },
        { title: "Scheduling & Phasing", body: "Develop realistic construction schedules and sequencing strategies." },
        { title: "Site Logistics Planning", body: "Coordinate staging areas, deliveries, access routes, and safety planning." },
        { title: "Bid Package Development", body: "Prepare bid packages and assist with subcontractor procurement." },
        { title: "Permitting Coordination", body: "Support permitting efforts and coordinate with local agencies when required." },
      ],
    },
    process: {
      eyebrow: "Our Process",
      title: "From concept to construction-ready.",
      steps: [
        { label: "Step 1", body: "Evaluate project goals, scope, and overall feasibility." },
        { label: "Step 2", body: "Develop conceptual budgets and cost analyses." },
        { label: "Step 3", body: "Review drawings and recommend value improvements." },
        { label: "Step 4", body: "Create schedules, logistics plans, and procurement strategies." },
        { label: "Step 5", body: "Coordinate permitting and prepare for construction." },
        { label: "Step 6", body: "Deliver a complete roadmap for successful project execution." },
      ],
    },
    lists: [
      {
        eyebrow: "Why Next Horizon Builders",
        title: "The value of planning first.",
        items: [
          "Early planning that minimizes costly surprises",
          "Accurate budgeting and scheduling",
          "Strong collaboration with owners and design teams",
          "Reduced construction risk",
          "Better cost control",
          "A smoother transition into construction",
        ],
      },
    ],
    cta: {
      title: "Build With Confidence From Day One",
      body: "Partner with Next Horizon Builders to establish a clear plan, realistic budget, and successful path forward before construction begins.",
      button: { label: "Request a Consultation", to: "/contact" },
    },
  },

  "ground-up-multifamily": {
    slug: "ground-up-multifamily",
    name: "Ground-Up Multifamily Construction",
    short: "Full-scope ground-up construction of multifamily communities—from site preparation to final turnover—managed with precision and craftsmanship.",
    image: IMAGES.multifamily[0],
    hero: {
      eyebrow: "Services",
      titleLines: ["Ground-Up Multifamily", "Construction"],
      tagline: "Building Communities from the Ground Up",
      description:
        "Next Horizon Builders specializes in delivering high-quality ground-up multifamily residential projects across Southern California. From early site preparation to final turnover, we manage every phase with precision, accountability, and craftsmanship.",
      cta: { label: "Request a Consultation", to: "/contact" },
    },
    intro: {
      eyebrow: "Overview",
      title: "Complete Ground-Up Construction Solutions",
      body: [
        "Ground-up multifamily construction requires careful planning, disciplined execution, and experienced leadership. Our team coordinates every stage of the project to ensure quality construction, efficient scheduling, and successful project delivery while maintaining the highest standards of safety and professionalism.",
      ],
    },
    cards: {
      eyebrow: "Our Services",
      title: "Every stage, expertly managed.",
      items: [
        { title: "Site Preparation", body: "Clearing, grading, utilities, excavation, and preparing the site for successful construction." },
        { title: "Structural Construction", body: "Concrete, foundations, structural framing, and complete building envelope construction." },
        { title: "Project Management", body: "Comprehensive oversight of scheduling, subcontractors, budgets, inspections, and daily field operations." },
        { title: "Quality Assurance", body: "Continuous inspections, workmanship verification, and strict quality control throughout construction." },
        { title: "Safety Management", body: "OSHA-compliant safety practices that protect workers, subcontractors, and every project site." },
        { title: "Project Closeout", body: "Final inspections, punch list completion, documentation, and seamless owner turnover." },
      ],
    },
    process: {
      eyebrow: "Our Process",
      title: "Foundation to final turnover.",
      steps: [
        { label: "Step 1", title: "Project Planning & Mobilization", body: "Preparing schedules, permits, logistics, and project teams." },
        { label: "Step 2", title: "Site Development", body: "Excavation, grading, underground utilities, and foundation work." },
        { label: "Step 3", title: "Building Construction", body: "Structural framing, roofing, exterior systems, and building enclosure." },
        { label: "Step 4", title: "MEP Installation", body: "Mechanical, electrical, plumbing, fire protection, and building systems." },
        { label: "Step 5", title: "Interior Finishes", body: "Drywall, flooring, cabinetry, painting, fixtures, and final finishes." },
        { label: "Step 6", title: "Final Turnover", body: "Testing, inspections, closeout documentation, and owner handover." },
      ],
    },
    lists: [
      {
        eyebrow: "Why Next Horizon Builders",
        title: "The builder owners rely on.",
        items: [
          "Specialized in Ground-Up Multifamily Construction",
          "Experienced Construction Professionals",
          "Transparent Communication",
          "Efficient Project Scheduling",
          "Superior Quality Control",
          "Safety-First Jobsite Culture",
          "Reliable Budget Management",
          "On-Time Project Delivery",
        ],
      },
    ],
    cta: {
      title: "Let's Build the Next Community Together",
      body: "Partner with Next Horizon Builders for dependable ground-up multifamily construction backed by experience, integrity, and exceptional project execution.",
      button: { label: "Start Your Project", to: "/contact" },
    },
  },

  "project-closeout": {
    slug: "project-closeout",
    name: "Project Closeout & Turnover",
    short: "A seamless closeout and turnover process ensuring every project is completed, documented, inspected, and ready for occupancy with confidence.",
    image: "/media/generated/project-closeout.jpg",
    listImage: "/media/generated/services-closeout-card.jpeg",
    hero: {
      eyebrow: "Services",
      titleLines: ["Project Closeout", "& Turnover"],
      tagline: "Successfully Closing Today, Building Long-Term Relationships for Tomorrow.",
      description:
        "Our commitment doesn't end when construction is complete. We deliver a seamless closeout and turnover process that ensures every project is fully completed, documented, inspected, and ready for occupancy with confidence.",
      cta: { label: "Request a Consultation", to: "/contact" },
    },
    intro: {
      eyebrow: "Overview",
      title: "A Seamless Finish to Every Project",
      body: [
        "Project closeout is one of the most important phases of construction. We carefully coordinate inspections, documentation, punch list completion, warranties, and final turnover to ensure our clients receive a project that meets the highest standards of quality, safety, and performance.",
      ],
    },
    cards: {
      eyebrow: "Our Closeout Services",
      title: "A meticulous handover.",
      items: [
        { title: "Punch List Management", body: "Identify, track, and complete all remaining work items before final acceptance." },
        { title: "Final Inspections", body: "Coordinate inspections with local agencies and ensure all approvals are obtained." },
        { title: "Project Documentation", body: "Provide warranties, operation manuals, maintenance information, and as-built documentation." },
        { title: "Commissioning Support", body: "Assist with equipment testing and system verification to ensure proper operation." },
        { title: "Warranty Coordination", body: "Deliver organized warranty documentation and provide post-completion support." },
        { title: "Owner Turnover", body: "Complete a structured handover process that prepares owners for successful occupancy." },
      ],
    },
    process: {
      eyebrow: "Our Process",
      title: "How we close out.",
      steps: [
        { label: "Step 1", title: "Review Project Completion", body: "Verify construction is complete and ready for final review." },
        { label: "Step 2", title: "Coordinate Final Inspections", body: "Schedule inspections and obtain all required approvals." },
        { label: "Step 3", title: "Complete Punch List", body: "Resolve outstanding items quickly and professionally." },
        { label: "Step 4", title: "Compile Documentation", body: "Organize warranties, manuals, as-built drawings, and closeout documents." },
        { label: "Step 5", title: "Owner Handover", body: "Deliver all documentation and transition the completed project to ownership." },
      ],
    },
    lists: [
      {
        eyebrow: "Why Next Horizon Builders",
        title: "Finished with excellence.",
        items: [
          "Thorough attention to detail",
          "Organized closeout process",
          "Transparent communication",
          "Complete project documentation",
          "Long-term warranty support",
          "Smooth owner transition",
        ],
      },
    ],
    cta: {
      title: "Ready to Deliver Excellence",
      body: "Our team ensures every project finishes as successfully as it began—with complete documentation, quality workmanship, and a seamless owner handover.",
      button: { label: "Request a Consultation", to: "/contact" },
    },
  },
};

export const SERVICE_LIST = Object.values(SERVICE_DETAILS);
